import { z } from 'zod'
import { type Locale, type Page, pageQuerySchema, versionSchema } from './common.js'

/**
 * Messaging (01 §8, ADR-029): an e-mail outbox written in the business transaction and delivered by the
 * `messaging.deliver` job. The outbox never holds a plain address: the recipient lives in the PII vault
 * as a token, beside a keyed hash for opt-out matching and a masked form for screens.
 */

export const MESSAGE_CHANNELS = ['email'] as const
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number]

/** SENT and SUPPRESSED are terminal; FAILED can be retried (back to QUEUED). */
export const MESSAGE_STATUSES = ['QUEUED', 'SENDING', 'SENT', 'FAILED', 'SUPPRESSED'] as const
export type MessageStatus = (typeof MESSAGE_STATUSES)[number]

/** TRANSACTIONAL messages (an invoice, an invitation) are never suppressed by an opt-out. */
export const MESSAGE_CATEGORIES = ['TRANSACTIONAL', 'MARKETING', 'NURTURE'] as const
export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number]

/**
 * Why a message failed or was suppressed: a closed set stored in `message_outbox.last_error_code`.
 * Adapters map every provider result onto it and never store raw provider text.
 */
export const MESSAGE_ERROR_CODES = {
  SMTP_REJECTED: {
    en: 'The mail server refused the message',
    ar: 'رفض خادم البريد الرسالة',
  },
  SMTP_UNAVAILABLE: {
    en: 'The mail server could not be reached',
    ar: 'تعذّر الوصول إلى خادم البريد',
  },
  AUTH_FAILED: {
    en: 'The mail server refused the user name or password',
    ar: 'رفض خادم البريد اسم المستخدم أو كلمة المرور',
  },
  TIMEOUT: { en: 'The mail server took too long to answer', ar: 'استغرق خادم البريد وقتًا طويلًا للرد' },
  DOCUMENT_FAILED: {
    en: 'The attached PDF could not be produced',
    ar: 'تعذّر إنشاء ملف PDF المرفق',
  },
  ADAPTER_NOT_ALLOWED_IN_ZONE: {
    en: 'This mail service is not allowed for the company’s data residency',
    ar: 'خدمة البريد هذه غير مسموح بها وفق موقع حفظ بيانات الشركة',
  },
  OPTED_OUT: {
    en: 'The recipient asked not to receive these messages',
    ar: 'طلب المستلم عدم استلام هذه الرسائل',
  },
  MODULE_OFF: {
    en: 'The module that sends it is turned off',
    ar: 'الوحدة التي ترسل الرسالة متوقفة',
  },
  NOT_CONFIGURED: {
    en: 'E-mail is not set up for this company',
    ar: 'البريد الإلكتروني غير مُعد لهذه الشركة',
  },
  UNKNOWN: { en: 'Unknown error', ar: 'خطأ غير معروف' },
} as const satisfies Record<string, { en: string; ar: string }>
export type MessageErrorCode = keyof typeof MESSAGE_ERROR_CODES
export const MESSAGE_ERROR_CODE_LIST = Object.keys(MESSAGE_ERROR_CODES) as MessageErrorCode[]

export function isMessageErrorCode(value: string): value is MessageErrorCode {
  return Object.hasOwn(MESSAGE_ERROR_CODES, value)
}

// ── outbox ───────────────────────────────────────────────────────────────────

export const MESSAGE_OUTBOX_SORT_FIELDS = ['createdAt', 'status', 'sentAt'] as const

/** GET /messaging/outbox. `q` matches the template key or the masked recipient. */
export const messageOutboxQuerySchema = pageQuerySchema.extend({
  status: z.enum(MESSAGE_STATUSES).optional(),
  channel: z.enum(MESSAGE_CHANNELS).optional(),
  subjectType: z
    .string()
    .trim()
    .regex(/^[a-z][a-z_]{0,39}$/)
    .optional(),
  subjectId: z.uuid().optional(),
})
export type MessageOutboxQuery = z.input<typeof messageOutboxQuerySchema>

/** One outbox row as screens show it: the recipient masked (`a***@example.com`), never the address. */
export interface OutboxItem {
  id: string
  channel: MessageChannel
  category: MessageCategory
  /** The module that produced it (ADR-024), e.g. `sales`; null for core messages. */
  module: string | null
  templateKey: string
  locale: Locale
  status: MessageStatus
  /** Null once the recipient was anonymised. */
  recipientMasked: string | null
  /** The business record it is about (e.g. `sales_invoice` and the invoice id). */
  subjectType: string | null
  subjectId: string | null
  attempts: number
  retryCount: number
  lastErrorCode: MessageErrorCode | null
  createdAt: string
  sentAt: string | null
  updatedAt: string
  version: number
}

/**
 * GET /messaging/outbox: a page of the filtered list, plus the count per status over the other filters
 * (the outbox tabs All / Failed / Queued / Suppressed show them).
 */
export interface MessageOutboxPage extends Page<OutboxItem> {
  counts: Record<MessageStatus, number>
}

// ── templates ────────────────────────────────────────────────────────────────

/** A template's text in one language: a subject line and a body of paragraphs with `{{var}}` slots. */
export const messageTemplateTextSchema = z.object({
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(10_000),
})
export type MessageTemplateText = z.output<typeof messageTemplateTextSchema>

/** PUT /messaging/templates/:key/:locale: a tenant override (a new version; `version` is the one read). */
export const messageTemplateOverrideSchema = messageTemplateTextSchema.extend({ version: versionSchema })
export type MessageTemplateOverride = z.input<typeof messageTemplateOverrideSchema>

/** POST /messaging/templates/:key/:locale/reset: back to the code default. */
export const messageTemplateResetSchema = z.object({ version: versionSchema })
export type MessageTemplateReset = z.input<typeof messageTemplateResetSchema>

export interface MessageTemplateLocaleState {
  locale: Locale
  /** The text shipped with the code. */
  default: MessageTemplateText
  /** The tenant's override, or null while the default applies. */
  override: (MessageTemplateText & { updatedAt: string; updatedBy: string | null }) | null
  /** Version of the key and locale's override history (1 before any override). */
  version: number
}

/** GET /messaging/templates and /messaging/templates/:key. */
export interface MessageTemplate {
  key: string
  module: string | null
  category: MessageCategory
  label: { en: string; ar: string }
  /** The variables the producer supplies; an override may use only these. */
  variables: string[]
  locales: MessageTemplateLocaleState[]
  /** Declared variables missing from an override (a warning, not an error). */
  warnings: TemplateParityIssue[]
}

// ── opt-outs (API only in R0b) ───────────────────────────────────────────────

/** POST /messaging/opt-outs: the address is hashed and tokenised; it is never listed back. */
export const messageOptOutCreateSchema = z.object({
  address: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  channel: z.enum(MESSAGE_CHANNELS).default('email'),
  category: z.enum(['MARKETING', 'NURTURE'] as const),
  reason: z.string().trim().max(500).nullish(),
})
export type MessageOptOutCreate = z.input<typeof messageOptOutCreateSchema>

export const messageOptOutQuerySchema = pageQuerySchema.extend({
  active: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
})
export type MessageOptOutQuery = z.input<typeof messageOptOutQuerySchema>

export interface MessageOptOut {
  id: string
  channel: MessageChannel
  category: MessageCategory
  recipientMasked: string | null
  createdAt: string
  revokedAt: string | null
  version: number
}

// ── test e-mail ──────────────────────────────────────────────────────────────

/** POST /messaging/test-email: sends `messaging.test` to the actor's own address. */
export const testEmailSchema = z.object({ locale: z.enum(['en', 'ar']).default('en') })
export type TestEmail = z.input<typeof testEmailSchema>

/** POST /messaging/test-email and GET /messaging/test-email/:id: where the actor's test e-mail is. */
export interface TestEmailStatus {
  id: string
  status: MessageStatus
  /** The actor's own address, masked. */
  recipientMasked: string | null
  lastErrorCode: MessageErrorCode | null
  createdAt: string
  sentAt: string | null
}

// ── template engine (shared by the code-template test, the API and the editor) ──

const VAR = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]{0,63})\s*\}\}/g

/** The variable names a text uses, once each, in order of first use. `{{ name }}` counts; `{x}` does not. */
export function templateVars(text: string): string[] {
  const seen = new Set<string>()
  for (const m of text.matchAll(VAR)) if (m[1]) seen.add(m[1])
  return [...seen]
}

export interface TemplateParityIssue {
  locale: Locale
  /** `unknown`: used but not declared (an error). `missing`: declared but unused (a warning). */
  kind: 'unknown' | 'missing'
  variable: string
}

/**
 * How the EN and AR texts (subject and body together) use the declared variables. An `unknown`
 * variable makes the template unusable (422 TEMPLATE_UNKNOWN_VARIABLE); a `missing` one is a warning.
 */
export function templateParity(
  en: string | null,
  ar: string | null,
  declared: readonly string[],
): { ok: boolean; issues: TemplateParityIssue[] } {
  const known = new Set(declared)
  const issues: TemplateParityIssue[] = []
  for (const [locale, text] of [
    ['en', en],
    ['ar', ar],
  ] as const) {
    if (text === null) continue
    const used = templateVars(text)
    for (const v of used) if (!known.has(v)) issues.push({ locale, kind: 'unknown', variable: v })
    const usedSet = new Set(used)
    for (const v of declared) if (!usedSet.has(v)) issues.push({ locale, kind: 'missing', variable: v })
  }
  return { ok: !issues.some((i) => i.kind === 'unknown'), issues }
}

/** Thrown by `renderTemplate` for a variable the caller did not supply. */
export class TemplateVariableError extends Error {
  constructor(readonly variable: string) {
    super(`Template variable "${variable}" is not declared`)
    this.name = 'TemplateVariableError'
  }
}

/**
 * Fills `{{name}}` slots with the given values, as plain text (HTML escaping is the caller's, when it
 * builds HTML). A slot whose name is not in `vars` throws; null values render empty.
 */
export function renderTemplate(text: string, vars: Readonly<Record<string, string | number | null>>): string {
  return text.replace(VAR, (_match, name: string) => {
    if (!Object.hasOwn(vars, name)) throw new TemplateVariableError(name)
    const value = vars[name]
    return value === null || value === undefined ? '' : String(value)
  })
}
